import { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import type { Contact, ContactGroup } from '@/lib/types';

/**
 * Everything the contacts manager reads for one company: its contacts, the
 * `/contact-groups` master for the group picker, and which contact is primary.
 *
 * ## ⚠️ THE FETCH IS KEYED ON THE ID, NOT ON AN OBJECT
 *
 * Same rule as `CompanyContactsMailModal`: callers compose
 * `{ id: row.id, name: row.name }` inline, so anything depending on that object
 * would refire on every parent render. Pass the number; `null` is closed and
 * reads nothing.
 *
 * ## ⚠️ DATA IS TAGGED WITH THE COMPANY IT BELONGS TO
 *
 * The loaded state carries its own `companyId` and the reload token it answered.
 * A read belonging to another company is never returned (the rows would sit
 * under the wrong company's name), and `loading` is derived from that tag rather
 * than from a flag, so the first frame after opening is "loading", never a false
 * "no contacts". Reopening on the same company shows what was loaded while the
 * fresh read replaces it.
 *
 * ## ⚠️ A FAILED GROUPS READ DOES NOT HIDE THE CONTACTS
 *
 * The three reads settle independently. Any failure is reported in `error`;
 * whatever did arrive is still returned.
 *
 * `truncated` is `meta.total` against what arrived (perPage is 100, the default
 * of 20 would truncate silently); local add/remove keep the total in step.
 */
export interface UseCompanyContactsResult {
  contacts: Contact[];
  groups: ContactGroup[];
  primaryContactId: number | null;
  loading: boolean;
  error: unknown | null;
  truncated: boolean;
  reload(): void;
  replaceContact(c: Contact): void;
  addContact(c: Contact): void;
  removeContact(id: number): void;
  setPrimaryContactId(id: number | null): void;
}

/** The ENTITIES-family ceiling `/contacts` belongs to. */
const CONTACTS_PER_PAGE = 100;
/** The reference-master ceiling `/contact-groups` belongs to. */
const CONTACT_GROUPS_PER_PAGE = 200;

interface LoadedState {
  companyId: number;
  token: number;
  contacts: Contact[];
  /** `meta.total` from the contacts read; `null` when that read failed. */
  total: number | null;
  groups: ContactGroup[];
  primaryContactId: number | null;
  error: unknown | null;
}

const NO_CONTACTS: Contact[] = [];
const NO_GROUPS: ContactGroup[] = [];

export function useCompanyContacts(companyId: number | null): UseCompanyContactsResult {
  const [reloadToken, setReloadToken] = useState(0);
  const [loaded, setLoaded] = useState<LoadedState | null>(null);

  useEffect(() => {
    if (companyId === null) return;

    const controller = new AbortController();
    const { signal } = controller;

    void Promise.allSettled([
      api.listContacts({ companyId, perPage: CONTACTS_PER_PAGE, sort: 'name', order: 'asc' }, signal),
      api.listContactGroups({ perPage: CONTACT_GROUPS_PER_PAGE }, signal),
      api.getCompany(companyId, signal),
    ]).then(([contactsResult, groupsResult, companyResult]) => {
      if (signal.aborted) return;
      const failure = [contactsResult, groupsResult, companyResult].find(
        (result): result is PromiseRejectedResult => result.status === 'rejected',
      );
      setLoaded((previous) => {
        /* Keep the previous read of the SAME company for any part that failed. */
        const same = previous !== null && previous.companyId === companyId ? previous : null;
        return {
          companyId,
          token: reloadToken,
          contacts:
            contactsResult.status === 'fulfilled'
              ? contactsResult.value.data
              : (same?.contacts ?? NO_CONTACTS),
          total:
            contactsResult.status === 'fulfilled'
              ? contactsResult.value.meta.total
              : (same?.total ?? null),
          groups:
            groupsResult.status === 'fulfilled'
              ? groupsResult.value.data
              : (same?.groups ?? NO_GROUPS),
          primaryContactId:
            companyResult.status === 'fulfilled'
              ? (companyResult.value.data.primaryContact?.id ?? null)
              : (same?.primaryContactId ?? null),
          error: failure === undefined ? null : (failure.reason as unknown),
        };
      });
    });

    return () => controller.abort();
  }, [companyId, reloadToken]);

  const current = loaded !== null && companyId !== null && loaded.companyId === companyId ? loaded : null;
  const loading = companyId !== null && (current === null || current.token !== reloadToken);
  const contacts = current?.contacts ?? NO_CONTACTS;
  const truncated = current !== null && current.total !== null && current.total > contacts.length;

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  /* Mutators touch only the read belonging to the open company. */
  const update = useCallback(
    (change: (state: LoadedState) => LoadedState) => {
      setLoaded((previous) =>
        previous !== null && previous.companyId === companyId ? change(previous) : previous,
      );
    },
    [companyId],
  );

  const replaceContact = useCallback(
    (contact: Contact) =>
      update((state) => ({
        ...state,
        contacts: state.contacts.map((existing) => (existing.id === contact.id ? contact : existing)),
      })),
    [update],
  );

  const addContact = useCallback(
    (contact: Contact) =>
      update((state) =>
        state.contacts.some((existing) => existing.id === contact.id)
          ? state
          : {
              ...state,
              contacts: [...state.contacts, contact],
              total: state.total === null ? null : state.total + 1,
            },
      ),
    [update],
  );

  const removeContact = useCallback(
    (id: number) =>
      update((state) =>
        state.contacts.some((existing) => existing.id === id)
          ? {
              ...state,
              contacts: state.contacts.filter((existing) => existing.id !== id),
              total: state.total === null ? null : Math.max(state.total - 1, 0),
              /* The server clears the company's primary when that contact goes. */
              primaryContactId: state.primaryContactId === id ? null : state.primaryContactId,
            }
          : state,
      ),
    [update],
  );

  const setPrimaryContactId = useCallback(
    (id: number | null) => update((state) => ({ ...state, primaryContactId: id })),
    [update],
  );

  return {
    contacts,
    groups: current?.groups ?? NO_GROUPS,
    primaryContactId: current?.primaryContactId ?? null,
    loading,
    error: current?.error ?? null,
    truncated,
    reload,
    replaceContact,
    addContact,
    removeContact,
    setPrimaryContactId,
  };
}
